/**
 * In-memory fake for the P14-07 seam.
 *
 * Two things it exists to let a consumer prove before the factory route lands:
 *
 *   **Publishing carries the accepted findings and nothing else.** `publishAccepted` counts
 *   the `accepted` rows, not the total, so a screen that forgot to send its rejections still
 *   publishes the right set here rather than appearing to work.
 *
 *   **A stale sequence is a conflict.** `publishAccepted` throws {@link
 *   StaleOptimisationPublishError} when the caller's `expectedLiveVersionSeq` is behind, which
 *   is the same 409 `publishAgentVersion` produces. The resolution is to read again, never to
 *   retry with the same number.
 *
 * Findings are seeded rather than generated: this fake stands in for the factory, and a fake
 * that invented findings would be making claims about calls nobody made.
 */
import { agentVersionId as agentVersionIdCodec, optimisationFindingId as optimisationFindingIdCodec, optimisationReviewId as optimisationReviewIdCodec, } from "../../id/codec.js";
import { CURRENCY_CODE } from "../../billing/money.js";
import { optimisationReviewPriceMinor, } from "../../agent/optimisation.js";
/** The design's own two prices, re-denominated to EUR (ADR 0014). */
export const FAKE_OPTIMISATION_REVIEW_PRICE_BOOK = Object.freeze({
    reviewStandardMinor: 240,
    reviewUltraMinor: 1_840,
    currency: CURRENCY_CODE,
});
export class StaleOptimisationPublishError extends Error {
    name = "StaleOptimisationPublishError";
    constructor(expected, actual) {
        super(`Somebody else published while these findings were being decided: the live version ` +
            `sequence is ${String(actual)}, not ${String(expected)}. Read the review again and ` +
            `publish from what you see; republishing with the same sequence would overwrite a ` +
            `decision you have not read.`);
    }
}
/** The four findings the design's own Review-and-optimise dialog lists. */
export const FAKE_FINDING_SEED = Object.freeze([
    {
        issue: "Gluten-free questions end the call",
        evidence: "In 14 conversations the caller asked for gluten-free options and the agent could only " +
            "say it did not know.",
        affectedConversationCount: 14,
        proposedFix: "Add the gluten-free base and its surcharge to the menu, and answer directly.",
    },
    {
        issue: "Delivery estimate is too optimistic on Fridays",
        evidence: "Friday orders quoted 35 minutes; the order book recorded an average of 52. Six people " +
            "rang back to chase.",
        affectedConversationCount: 6,
        proposedFix: "Quote 50 minutes between 18:00 and 21:00 on Fridays and Saturdays.",
    },
    {
        issue: "Prices read awkwardly in Spanish",
        evidence: 'The agent reads "31.10" as digits rather than as money in 9 Spanish calls.',
        affectedConversationCount: 9,
        proposedFix: "Read totals as currency in every language, matching the caller's locale.",
    },
    {
        issue: "Refund requests reach the kitchen",
        evidence: "Three refund requests were transferred to the order line instead of billing.",
        affectedConversationCount: 3,
        proposedFix: "Route anything mentioning a refund or a chargeback to billing.",
    },
]);
export class FakeOptimisationReviewPort {
    #deps;
    #priceBook;
    #held = new Map();
    #liveSeq = new Map();
    /** How many conversations are in scope, and how many of them still have audio. */
    conversationCount = 1_284;
    recordingAvailableCount = 1_284;
    constructor(deps, priceBook = FAKE_OPTIMISATION_REVIEW_PRICE_BOOK) {
        this.#deps = deps;
        this.#priceBook = priceBook;
    }
    /** The VoiceAgent's live sequence, so the conflict path is reachable in a suite. */
    setLiveVersionSeq(voiceAgentId, seq) {
        this.#liveSeq.set(voiceAgentId, seq);
    }
    quote(_scope, _voiceAgentId, depth, conversationScope) {
        const unavailableReason = this.conversationCount === 0
            ? "no_conversations_in_scope"
            : depth === "ultra" && this.recordingAvailableCount === 0
                ? "recordings_expired"
                : null;
        return Promise.resolve({
            depth,
            scope: conversationScope,
            conversationCount: this.conversationCount,
            priceMinor: optimisationReviewPriceMinor(this.#priceBook, depth),
            currency: this.#priceBook.currency,
            recordingAvailableCount: this.recordingAvailableCount,
            unavailableReason,
        });
    }
    start(scope, request) {
        const optimisationReviewId = optimisationReviewIdCodec.next(this.#deps.clock, this.#deps.random);
        const at = new Date(this.#deps.clock.now());
        const billing = request.billing;
        const optimisationReview = {
            accountId: scope.accountId,
            optimisationReviewId,
            workspaceId: scope.workspaceId,
            voiceAgentId: request.voiceAgentId,
            depth: request.source.depth,
            scope: request.scope,
            conversationCount: request.source.transcript.length,
            state: "done",
            payer: billing.payer,
            absorbedReason: billing.payer === "wetalk" ? billing.absorbedReason : null,
            priceMinor: billing.amountMinor,
            currency: billing.currency,
            requestedByPersonId: null,
            requestedByOperatorId: null,
            publishedAgentVersionId: null,
            startedAt: at,
            finishedAt: at,
        };
        const finding = FAKE_FINDING_SEED.map((seed) => ({
            accountId: scope.accountId,
            optimisationFindingId: optimisationFindingIdCodec.next(this.#deps.clock, this.#deps.random),
            optimisationReviewId,
            issue: seed.issue,
            evidence: seed.evidence,
            affectedConversationCount: seed.affectedConversationCount,
            proposedFix: seed.proposedFix,
            // Nothing is accepted by default: publishing must be a decision, not an omission.
            decision: "pending",
            decidedAt: null,
        }));
        this.#held.set(optimisationReviewId, { optimisationReview, finding });
        return Promise.resolve(this.#detail(optimisationReviewId));
    }
    #detail(optimisationReviewId) {
        const held = this.#held.get(optimisationReviewId);
        if (held === undefined) {
            throw new Error(`No optimisation_review ${optimisationReviewId} in this fake.`);
        }
        return {
            optimisationReview: held.optimisationReview,
            finding: held.finding,
            acceptedCount: held.finding.filter((one) => one.decision === "accepted").length,
        };
    }
    getDetail(_scope, optimisationReviewId) {
        return Promise.resolve(this.#detail(optimisationReviewId));
    }
    decideFinding(_scope, optimisationFindingId, decision) {
        for (const held of this.#held.values()) {
            const at = held.finding.findIndex((one) => one.optimisationFindingId === optimisationFindingId);
            const found = held.finding[at];
            if (at < 0 || found === undefined)
                continue;
            held.finding[at] = {
                ...found,
                decision,
                decidedAt: decision === "pending" ? null : new Date(this.#deps.clock.now()),
            };
            return Promise.resolve(this.#detail(held.optimisationReview.optimisationReviewId));
        }
        return Promise.reject(new Error(`No optimisation_finding ${optimisationFindingId} in this fake.`));
    }
    publishAccepted(_scope, optimisationReviewId, expectedLiveVersionSeq) {
        const held = this.#held.get(optimisationReviewId);
        if (held === undefined) {
            return Promise.reject(new Error(`No optimisation_review ${optimisationReviewId} in this fake.`));
        }
        const voiceAgentId = held.optimisationReview.voiceAgentId;
        const actual = this.#liveSeq.get(voiceAgentId) ?? expectedLiveVersionSeq;
        if (actual !== expectedLiveVersionSeq) {
            return Promise.reject(new StaleOptimisationPublishError(expectedLiveVersionSeq, actual));
        }
        const made = agentVersionIdCodec.next(this.#deps.clock, this.#deps.random);
        const liveVersionSeq = actual + 1;
        this.#liveSeq.set(voiceAgentId, liveVersionSeq);
        held.optimisationReview = {
            ...held.optimisationReview,
            publishedAgentVersionId: made,
        };
        return Promise.resolve({
            optimisationReviewId,
            agentVersionId: made,
            liveVersionSeq,
            appliedFindingCount: held.finding.filter((one) => one.decision === "accepted").length,
        });
    }
}
//# sourceMappingURL=fake-optimisation-review.js.map